import { useCallback, useEffect, useRef, useState } from 'react';
import { Download, Upload, Type } from 'lucide-react';

const CANVAS_WIDTH = 800;
const CANVAS_HEIGHT = 800;

/**
 * 電商編輯器：Fabric.js 畫布，上傳底圖、加文字、輸出 PNG
 */
export function EcommerceEditorTool() {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<import('fabric').Canvas | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const init = async () => {
      const { Canvas } = await import('fabric');
      const canvasEl = document.createElement('canvas');
      canvasEl.width = CANVAS_WIDTH;
      canvasEl.height = CANVAS_HEIGHT;
      el.appendChild(canvasEl);
      const canvas = new Canvas(canvasEl, {
        width: CANVAS_WIDTH,
        height: CANVAS_HEIGHT,
        backgroundColor: '#fff',
      });
      canvasRef.current = canvas;
      setReady(true);
    };
    init();
    return () => {
      canvasRef.current?.dispose();
      canvasRef.current = null;
      while (el.firstChild) el.removeChild(el.firstChild);
    };
  }, []);

  const handleAddImage = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !file.type.startsWith('image/') || !canvasRef.current)
        return;
      setError(null);
      const url = URL.createObjectURL(file);
      import('fabric').then(({ FabricImage }) => {
        FabricImage.fromURL(url, { crossOrigin: 'anonymous' })
          .then(img => {
            URL.revokeObjectURL(url);
            const canvas = canvasRef.current!;
            const scale = Math.min(
              CANVAS_WIDTH / (img.width ?? 1),
              CANVAS_HEIGHT / (img.height ?? 1),
              1
            );
            img.scale(scale);
            img.set({ left: 0, top: 0 });
            canvas.add(img);
            canvas.sendObjectToBack(img);
            canvas.requestRenderAll();
          })
          .catch(() => {
            URL.revokeObjectURL(url);
            setError('圖片載入失敗');
          });
      });
      e.target.value = '';
    },
    []
  );

  const handleAddText = useCallback(async () => {
    if (!canvasRef.current) return;
    const { FabricText } = await import('fabric');
    const text = new FabricText('請編輯文字', {
      left: 50,
      top: 50,
      fontSize: 48,
      fill: '#000',
    });
    canvasRef.current.add(text);
    canvasRef.current.setActiveObject(text);
    canvasRef.current.requestRenderAll();
  }, []);

  const handleExport = useCallback(() => {
    if (!canvasRef.current) return;
    setError(null);
    const dataUrl = canvasRef.current.toDataURL({
      format: 'png',
      multiplier: 1,
    });
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = 'ecommerce-image.png';
    a.click();
  }, []);

  return (
    <>
      <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <label className="inline-flex cursor-pointer items-center gap-2 rounded bg-primary px-4 py-2 text-white hover:bg-primary/90">
            <Upload className="h-4 w-4" />
            <span>加入底圖</span>
            <input
              type="file"
              accept="image/*"
              onChange={handleAddImage}
              className="hidden"
              aria-label="加入底圖"
            />
          </label>
          <button
            type="button"
            onClick={handleAddText}
            disabled={!ready}
            className="inline-flex items-center gap-2 rounded bg-gray-700 px-4 py-2 text-sm text-white hover:bg-gray-600 disabled:opacity-50 dark:bg-gray-600 dark:hover:bg-gray-500"
          >
            <Type className="h-4 w-4" />
            加文字
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={!ready}
            className="inline-flex items-center gap-2 rounded bg-primary px-4 py-2 text-white hover:bg-primary/90 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            輸出 PNG
          </button>
        </div>
        {error && (
          <p
            className="mb-2 text-sm text-red-600 dark:text-red-400"
            role="alert"
          >
            {error}
          </p>
        )}
        <div className="overflow-auto rounded border border-gray-200 bg-gray-100 dark:border-gray-600 dark:bg-gray-900">
          <div
            ref={containerRef}
            className="mx-auto bg-white"
            style={{ width: CANVAS_WIDTH, height: CANVAS_HEIGHT }}
          >
            {!ready && (
              <div className="flex h-full items-center justify-center text-gray-500">
                載入編輯器…
              </div>
            )}
          </div>
        </div>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          加入底圖後可拖曳、縮放；加文字後可點選文字拖曳或雙擊編輯。輸出為
          800×800 px PNG。
        </p>
      </div>

      <section className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
        <h2 className="mb-3 text-lg font-semibold text-gray-900 dark:text-gray-100">
          使用說明
        </h2>
        <p className="leading-relaxed text-gray-600 dark:text-gray-400">
          上傳產品圖作為底圖，再新增文字標語或價格，拖曳調整位置後輸出
          PNG，適合社群貼文、電商主圖。所有操作均在瀏覽器內完成。
        </p>
      </section>
    </>
  );
}
